import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import type {
  AcademicLevel,
  CapCcdAnnexEntry,
  CapCompliancePacketDigest,
  CapMatrixAnnex,
} from '@dosfilos/domain';
import { formatDate } from '../../../lib/i18n-format';

// ─── La evidencia, del lado del revisor (CAP Fase 2) ────────────────────────
//
// Antes de esto el revisor veía trece secciones con tick verde, la frase
// "respaldada por una auditoría de cumplimiento" y una atestación que decía que
// el centro sabía que había brechas — sin ver CUÁLES. Aprobar otorga la
// certificación, así que se le pedía certificar sin poder verificar.
//
// Las etiquetas de la auditoría (área, mínimo, actual, brecha, PLOs, requisitos
// no académicos) se toman del namespace `cap`, el mismo que usa la pantalla de
// cumplimiento del centro: dos juegos de palabras para la misma tabla es la
// forma más barata de que el revisor y el centro discutan por una traducción.

/** Qué parte de la evidencia mostrar. El expediente pasó a leerse por pestañas,
 *  y cada una pide UN bloque; sin esto habría que duplicar las tres tablas en
 *  otro archivo, que es como el papel y la pantalla empiezan a divergir. */
export type CapEvidenceBlock = 'audit' | 'ccds' | 'matrix';

export function CapEvidencePanel({
  compliance,
  ccdAnnex,
  matrixAnnex,
  level,
  only,
}: {
  compliance: CapCompliancePacketDigest | undefined;
  ccdAnnex: CapCcdAnnexEntry[] | undefined;
  matrixAnnex: CapMatrixAnnex | undefined;
  level: AcademicLevel;
  /** Omitido = los tres bloques, uno debajo del otro (como antes). */
  only?: CapEvidenceBlock;
}) {
  const { t } = useTranslation('sis');
  const { t: tc } = useTranslation('cap');

  // Una ronda anterior a 2026-09-17 cruzó sin evidencia. Decirlo es distinto de
  // mostrar una tabla en cero, que se leería como "medido y vacío".
  if (!compliance) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        {t('network.review.cap.evidence.missing')}
      </Alert>
    );
  }

  const plosEvaluated = compliance.plos.filter((p) => p.evaluated).length;
  const plosPending = compliance.plos.filter((p) => !p.evaluated);
  const nonAcademicUnmet = compliance.nonAcademic.filter((n) => !n.met);

  const shows = (block: CapEvidenceBlock): boolean => !only || only === block;

  // Una pestaña vacía no explica nada: el revisor no sabe si el centro no lo
  // mandó o si la pantalla falló. Cuando el bloque pedido no tiene datos, se
  // dice — el anexo 4 además es RECOMENDADO, no obligatorio, y esa diferencia
  // cambia lo que el revisor debería reclamar.
  const emptyBlock =
    (only === 'ccds' && !(ccdAnnex && ccdAnnex.length > 0)) ||
    (only === 'matrix' && !(matrixAnnex && matrixAnnex.rows.length > 0));
  if (emptyBlock) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t(`network.review.cap.evidence.${only === 'ccds' ? 'noCcdAnnex' : 'noMatrixAnnex'}`)}
      </Alert>
    );
  }

  return (
    <>
      {shows('audit') && (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('network.review.cap.evidence.title')}
            </Typography>
            <Chip
              size="small"
              color={compliance.overallStatus === 'compliant' ? 'success' : 'warning'}
              label={tc(`compliance.status.${compliance.overallStatus}`)}
            />
            <Chip
              size="small"
              variant="outlined"
              color={compliance.totalActualTcu >= compliance.totalMinTcu ? 'default' : 'error'}
              label={`${compliance.totalActualTcu} / ${compliance.totalMinTcu} TCU`}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t('network.review.cap.evidence.ranOn', {
              date: formatDate(compliance.generatedAt, {
                dateStyle: 'long',
              }),
              framework: compliance.frameworkVersion,
            })}
            {' · '}
            {compliance.stackedPriorCapRecognized
              ? t('network.review.cap.evidence.stacked')
              : t('network.review.cap.evidence.freeStanding')}
          </Typography>

          <Table size="small" sx={{ mt: 1.5 }}>
            <TableHead>
              <TableRow>
                <TableCell>{tc('compliance.area')}</TableCell>
                <TableCell align="right">{tc('compliance.minTcu')}</TableCell>
                <TableCell align="right">{tc('compliance.actualTcu')}</TableCell>
                <TableCell>{tc('compliance.state')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {compliance.subjectAreas.map((a) => (
                <TableRow key={a.code}>
                  <TableCell>{tc(`subjectAreas.${a.code}`)}</TableCell>
                  <TableCell align="right">{a.minTcu}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: a.met ? 400 : 700 }}>
                    {a.actualTcu}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={a.met ? 'success' : 'error'}
                      label={
                        a.met ? tc('compliance.met') : tc('compliance.missing', { tcu: a.gapTcu })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{tc('compliance.areaTotal')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {compliance.totalMinTcu}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {compliance.totalActualTcu}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />

          {/* Los resultados de aprendizaje: la compuerta que ninguna sección
            completa puede sustituir. Una E sale de una tarea signature real. */}
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {tc('compliance.ploCoverage')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`${plosEvaluated} / ${compliance.plos.length} ${tc('compliance.evaluated')}`}
            {/* La lista que sigue son los que FALTAN. Sin decirlo se leía como
                si fueran los evaluados — justo al revés. */}
            {plosPending.length > 0
              ? ` · ${tc('compliance.plosPendingList', {
                  plos: plosPending.map((p) => p.sourceOutcomeId).join(', '),
                })}`
              : ''}
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 700, mt: 1.5 }}>
            {tc('compliance.nonAcademic')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {compliance.nonAcademic.map((n) => (
              <Chip
                key={n.key}
                size="small"
                color={n.met ? 'success' : 'error'}
                variant={n.met ? 'outlined' : 'filled'}
                label={tc(`compliance.nonAcademicKeys.${n.key}`)}
              />
            ))}
          </Stack>

          {(compliance.coursesWithoutCcd.length > 0 ||
            compliance.coursesWithoutContribution.length > 0 ||
            compliance.coursesOutsideCapAreas.length > 0 ||
            compliance.stackingRequestedButNotSealed) && (
            <Box sx={{ mt: 1.5 }}>
              {compliance.coursesWithoutCcd.length > 0 && (
                <Typography variant="body2" color="error">
                  {tc('compliance.coursesWithoutCcd', {
                    courses: compliance.coursesWithoutCcd.map((c) => c.courseName).join(', '),
                  })}
                </Typography>
              )}
              {compliance.coursesWithoutContribution.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {tc('compliance.warnCoursesWithoutContribution', {
                    count: compliance.coursesWithoutContribution.length,
                  })}{' '}
                  {compliance.coursesWithoutContribution.map((c) => c.courseName).join(', ')}
                </Typography>
              )}
              {compliance.coursesOutsideCapAreas.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {tc('compliance.warnCoursesOutsideCapAreas', {
                    courses: compliance.coursesOutsideCapAreas.map((c) => c.courseName).join(', '),
                  })}
                </Typography>
              )}
              {compliance.stackingRequestedButNotSealed && (
                <Typography variant="body2" color="error">
                  {tc('compliance.warnStackingUnsealed')}
                </Typography>
              )}
            </Box>
          )}

          {nonAcademicUnmet.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('network.review.cap.evidence.nonAcademicPending', { n: nonAcademicUnmet.length })}
            </Alert>
          )}
        </Paper>
      )}

      {/* Attachment 2 — las fichas de catálogo, curso por curso. */}
      {shows('ccds') && ccdAnnex && ccdAnnex.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('network.review.cap.evidence.ccdTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('network.review.cap.evidence.ccdCount', { n: ccdAnnex.length })}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {ccdAnnex.map((entry) => (
              <Accordion key={entry.courseId} disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {entry.courseCode ? `${entry.courseCode} · ` : ''}
                      {entry.courseName}
                    </Typography>
                    {entry.ccd?.capSubjectArea && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={tc(`subjectAreas.${entry.ccd.capSubjectArea}`)}
                      />
                    )}
                    {entry.ccd?.tcu[level] !== undefined && (
                      <Chip size="small" label={`${entry.ccd.tcu[level]} TCU`} />
                    )}
                    {!entry.ccd && (
                      <Chip
                        size="small"
                        color="error"
                        label={t('network.review.cap.evidence.noCcd')}
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                {entry.ccd && (
                  <AccordionDetails>
                    {entry.ccd.description && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {entry.ccd.description}
                      </Typography>
                    )}
                    {entry.ccd.clos && entry.ccd.clos[level].length > 0 && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 1 }}>
                          {t('network.review.cap.evidence.clos')}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 3 }}>
                          {entry.ccd.clos[level].map((clo) => (
                            <Typography key={clo} component="li" variant="body2">
                              {clo}
                            </Typography>
                          ))}
                        </Box>
                      </>
                    )}
                  </AccordionDetails>
                )}
              </Accordion>
            ))}
          </Box>
        </Paper>
      )}

      {/* Attachment 4 — la matriz. Las marcas son del centro; la E la sostiene
          una tarea, y su nombre viaja para que el revisor pueda pedirla. */}
      {shows('matrix') && matrixAnnex && matrixAnnex.rows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('network.review.cap.evidence.matrixTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('network.review.cap.evidence.matrixLegend')}
          </Typography>
          <Box sx={{ overflowX: 'auto', mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tc('matrix.courseHeader')}</TableCell>
                  {matrixAnnex.outcomes.map((o) => (
                    <TableCell key={o.sourceOutcomeId} align="center">
                      {o.sourceOutcomeId}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixAnnex.rows.map((r) => (
                  <TableRow key={r.courseId}>
                    <TableCell>{r.courseName}</TableCell>
                    {matrixAnnex.outcomes.map((o) => {
                      const cell = r.cells.find((c) => c.sourceOutcomeId === o.sourceOutcomeId);
                      return (
                        <TableCell key={o.sourceOutcomeId} align="center">
                          <Typography
                            variant="body2"
                            title={
                              cell?.evaluatedBy.length
                                ? tc('matrix.evaluatedBy', {
                                    assignments: cell.evaluatedBy.join(', '),
                                  })
                                : undefined
                            }
                          >
                            {cell?.marks.join(' ') || '—'}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </>
  );
}
